import { useNavigate, useParams } from 'react-router-dom';
import React from 'react';
import { useMutation, useQuery } from 'react-query';
import * as yup from 'yup';
import { useFormik } from 'formik';
import clientByIdDummy from './clientById.json';
import api from '../../../../components/api';
import { UpdateClientData } from '../../../../components/api/clients';
import { yupErrorMsg } from '../../../../components/atoms/const';

interface ClientById {
  id: number;
  name: string;
  address: string;
  contactPerson: typeof clientByIdDummy.contactPerson;
  partners: typeof clientByIdDummy.partner;
  vmList: typeof clientByIdDummy.vendingMachines;
  categoryId: number;
  commission: number;
}

export interface Invoice {
  'serviceId': number,
  'billAmount': number,
  'discount': number,
  'paidAmount': number,
  'description': string,
  'paymentDate': string,
  'serviceDate': string,
  'cancel': string,
  'parts': any[],
  'billDate': string,
  'laborHour': number,
  'laborRate': any,
  'id': number
}

export interface CoffeeInvoice {
  'clientId': number,
  'billAmount': number,
  'paidAmount': number,
  'dropDate': string,
  'clientName': string,
  'billDate': number,
  'paidDate': string,
  'discountAmount': number,
  'cancel': string,
  'vmId': number,
  'id': number
}

interface PaymentDataType {
  pending: number;
  received: number;
}

function useIdClient() {
  /*
   *
   *
   *
   *
   *
   *
   *
   */

  /*
  * Navigation Handler
  */

  // get clientId from url params
  // url format : clients/:clientId
  const { clientId } = useParams();

  const navigate = useNavigate();
  // Navigate to client list page
  const onNavigateBack = () => {
    navigate('/clients');
  };
  // Navigate to Paid Invoice
  const navigateToViewCoffeeInvoices = () => {
    navigate(`/clients/coffee-invoice/${clientId}`);
  };
  // Navigate to Paid Invoice
  const navigateToViewPaidInvoices = () => {
    navigate(`/clients/invoice/${clientId}`);
  };
  // Navigate to Add Contact
  const navigateToAddContact = () => {
    navigate(`/clients/contact/${clientId}`);
  };
  // Navigate to Add Partner
  const navigateToAddPartner = () => {
    navigate(`/clients/partner/${clientId}`);
  };
  // Navigate to Edit Client
  const navigateToEditClient = () => {
    navigate(`/clients/add/${clientId}`);
  };

  const navigateToViewCoffeeInvoiceDetail = (invoiceId: number) => {
    console.log('Navigating coffee invoice...');
    navigate(`/clients/view-coffee-invoice/${clientId}/${invoiceId}`);
  };

  /*
  *
  *
  *
  *
  *
  *
  *
  */

  /*
  * Get Initial Data
  * 1. Because on Client Detail cant find any invoice data, we need to fetch it with invoice API
  * 2. Getting total payment data from filtered invoice list depends on paymentDate, if paymentDate
  *    is null, this mean, unpaid
  * 3. Because on Client Detail only show role on number code, we need to find the string format
  *    from getAllRoles API
  * 4. After some process to manage clientById data, we finally can get clientById by param clientId
  * Note : param from url format : clients/:clientId
  */

  // 1. Get Invoice List by Client Name
  const [clientName, setClientName] = React.useState<string | undefined>();
  const { data: invoicesRes, isLoading: invoicesLoading, refetch: refetchInvoices } = useQuery(['getServiceInvoicesByClientName', 'clients', clientName], () => api.clients.getServiceInvoicesByClientName(clientName!), {
    enabled: !!clientName,
  });
  const invoiceList: Invoice[] | [] = React.useMemo(() => {
    const resData: Invoice[] | undefined = invoicesRes?.data?.data;
    console.log(resData?.length);
    if (resData) { return resData.filter((pending) => !pending.paymentDate || (pending.paymentDate && pending.billAmount > pending.paidAmount)); }
    return [];
  }, [invoicesRes]);

  // 2. Get Total Payment Data
  const paymentData : PaymentDataType = React.useMemo(() => {
    const resData: Invoice[] | undefined = invoicesRes?.data?.data;
    let received = 0;
    let pending = 0;
    if (resData) {
      for (let i = 0; i < resData.length; i += 1) {
        received += (resData[i].paidAmount ?? 0);
        pending += (resData[i].billAmount ?? 0);
      }
    }
    return {
      received,
      pending: ((pending - received) < 0) ? 0 : (pending - received),
    };
  }, [invoicesRes]);

  // 1. Get Invoice List by Client Name
  const { data: coffeeInvoicesRes, isLoading: coffeeInvoicesLoading, refetch: refetchCoffeeInvoices } = useQuery(['coffee-invoices-by-client', 'clients', clientId], () => api.clients.getClientCoffeeInvoices(clientId!), {
    enabled: !!clientId,
  });

  const coffeeInvoiceList: CoffeeInvoice [] | [] = React.useMemo(() => {
    const resData: CoffeeInvoice [] | undefined = coffeeInvoicesRes?.data?.data;
    console.log(resData?.length);
    if (resData) { return resData.filter((pending) => !pending.paidDate || (pending.paidDate && pending.billAmount > pending.paidAmount)); }
    return [];
  }, [coffeeInvoicesRes]);

  const coffeePaymentData : PaymentDataType = React.useMemo(() => {
    const resData: CoffeeInvoice[] | undefined = coffeeInvoicesRes?.data?.data;
    let received = 0;
    let pending = 0;
    if (resData) {
      for (let i = 0; i < resData.length; i += 1) {
        received += (resData[i].paidAmount ?? 0);
        pending += (resData[i].billAmount ?? 0);
      }
    }
    return {
      received,
      pending: ((pending - received) < 0) ? 0 : (pending - received),
    };
  }, [coffeeInvoicesRes]);

  // 3. Get All Roles
  // const { data: rolesRes } = useQuery(['getAllRoles', 'user'], api.user.getAllRoles);
  // const roleList = React.useMemo(() => rolesRes?.data?.data, [rolesRes]);

  // 4. Manage Client By Id
  const {
    data, refetch: refetchGetClientById,
  } = useQuery(['getById', 'clients', clientId], () => api.clients.getById(clientId!), {
    enabled: !!clientId,
  });
  const clientById: ClientById | undefined = React.useMemo(() => {
    const resData: typeof clientByIdDummy | undefined = data?.data?.data;

    if (resData) {
      setClientName(resData.name);
      const addressFormat = `${resData.address.streetAddress}, ${resData.address.city}, ${resData.address.state}, ${resData.address.zipCode}`;
      return {
        id: resData.id,
        name: resData.name,
        address: addressFormat,
        contactPerson: resData.contactPerson,
        partners: resData.partner,
        vmList: resData.vendingMachines,
        categoryId: resData.categoryId,
        commission: resData.commission,
      };
    }
    return undefined;
  }, [data]);

  /*
   *
   *
   *
   *
   *
   *
   *
   */

  /*
  * Do Update Data : Delete Contact, Delete Partner, Delete Client, and Pay Invoice
  * 1. Delete Contact
  * 2. Delete Partner
  * 3. Pay Invoice
  * 4. Delete Client
  * Note : All Delete function will also help on handling modal
  * Result of API delete, will store into resultDelete state management.
  * Every time after close modal, will reset resultDelete state
  */

  // Result handler, later will help to know more dynamic response of modal
  // Will auto reset if modal closed
  const [resultUpdate, setResultUpdate] = React.useState();

  // 1. Delete Contact
  const { isLoading: loadingDeleteContact, mutate: deleteContact } = useMutation(api.clients.deleteContact, {
    onMutate: (x) => console.log('onMutate', x),
    onSuccess: (res: any) => {
      console.log(res);
      setResultUpdate(res);
    },
    onError: (err: any) => {
      console.log('err', err);
      setResultUpdate(err);
    },
  });
  const handleDeleteContact = (contactId: number) => {
    deleteContact(contactId);
  };

  // 2. Delete Partner
  const { isLoading: loadingDeletePartner, mutate: deletePartner } = useMutation(api.clients.update, {
    onMutate: (x) => console.log('onMutate', x),
    onSuccess: (res: any) => {
      console.log(res);
      setResultUpdate(res);
    },
    onError: (err: any) => {
      console.log('err', err);
      setResultUpdate(err);
    },
  });
  const handleDeletePartner = () => {
    if (clientById) {
      const clientData: UpdateClientData = {
        id: clientById.id,
        name: clientById.name,
        addressChanged: false,
        categoryId: clientById.categoryId,
        commision: clientById.commission,
        partnerId: null,
        partnerCommission: null,
      };
      deletePartner(clientData);
    }
  };

  // 3. Pay Invoice
  const { isLoading: loadingPayInvoice, mutate: payInvoice } = useMutation(api.clients.payServiceInvoice, {
    onMutate: (x) => console.log('onMutate', x),
    onSuccess: (res: any) => {
      console.log(res);
      setResultUpdate(res);
    },
    onError: (err: any) => {
      console.log('err', err);
      setResultUpdate(err);
    },
  });

  const { isLoading: loadingPayCoffeeInvoice, mutate: payCoffeeInvoice } = useMutation(api.clients.payCoffeeInvoice, {
    onMutate: (x) => console.log('onMutate', x),
    onSuccess: (res: any) => {
      console.log(res);
      setResultUpdate(res);
    },
    onError: (err: any) => {
      console.log('err', err);
      setResultUpdate(err);
    },
  });

  const yupSchema = yup.object({
    paidAmount: yup.number().min(0, yupErrorMsg.PAID_AMOUNT_POSITIVE).required(yupErrorMsg.PAID_AMOUNT_EMPTY),
  });

  /* form handler */
  const formikPayInvoice = useFormik({
    initialValues: {
      id: 0,
      paidAmount: 0,
    },
    validationSchema: yupSchema,
    onSubmit: (values) => {
      payInvoice(values);
    },
  });

  const formikPayCoffeeInvoice = useFormik({
    initialValues: {
      id: 0,
      paidAmount: 0,
    },
    validationSchema: yupSchema,
    onSubmit: (values) => {
      payCoffeeInvoice(values);
    },
  });

  // 3. Pay Invoice
  const { isLoading: deleteClientLoad, mutate: deleteClient } = useMutation(api.clients.remove, {
    onMutate: (x) => console.log('onMutate', x),
    onSuccess: (res: any) => {
      console.log('suc delete client', res);
      setResultUpdate(res);
    },
    onError: (err: any) => {
      console.log('err delete client,', err);
      setResultUpdate(err);
    },
  });

  // 4. Delete Client
  const handleDeleteClient = () => {
    if (clientId)deleteClient(parseInt(clientId, 10));
  };
  // This is unique case, because url should not be work again because clientById will always undefined after deleted
  const afterDeleteClient = () => {
    setResultUpdate(undefined);
    onNavigateBack();
  };

  // Do Refetch for update the data whenever success on update data
  const afterUpdate = () => {
    setResultUpdate(undefined);
    refetchGetClientById().catch((e) => alert(e));
    refetchInvoices().catch((e) => alert(e));
  };

  // Do Refetch for update the data whenever success on update data
  const afterCoffeeInvoiceUpdate = () => {
    setResultUpdate(undefined);
    refetchCoffeeInvoices().catch((e) => alert(e));
  };

  return {
    clientId,
    onNavigateBack,
    clientById,
    navigateToViewPaidInvoices,
    navigateToViewCoffeeInvoices,
    navigateToViewCoffeeInvoiceDetail,
    navigateToAddContact,
    navigateToAddPartner,
    navigateToEditClient,
    resultUpdate,
    loadingDeleteContact,
    handleDeleteContact,
    loadingDeletePartner,
    handleDeletePartner,
    afterUpdate,
    afterCoffeeInvoiceUpdate,
    invoiceList,
    invoicesLoading,
    coffeeInvoiceList,
    coffeeInvoicesLoading,
    paymentData,
    coffeePaymentData,
    formikPayInvoice,
    formikPayCoffeeInvoice,
    loadingPayInvoice,
    loadingPayCoffeeInvoice,
    //  roleList,
    handleDeleteClient,
    afterDeleteClient,
    deleteClientLoad,
  };
}

export default useIdClient;
